import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Users, Briefcase, Building2, Wallet,
  TrendingUp, PieChart, Settings, FileText, CreditCard, X,
  Clock, ShieldCheck, BarChart3, UserCog, Lock, Megaphone,
  Percent, Receipt, LineChart, ScrollText, FilePen, Link, Layers, Download, CandlestickChart, Zap, GraduationCap, Handshake
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, module: 'dashboard' },
  { path: '/branches', label: 'Branches', icon: Building2, module: 'branches' },
  { path: '/clients', label: 'Clients', icon: Users, module: 'clients' },
  { path: '/employees', label: 'Employees', icon: UserCog, module: 'employees' },
  { path: '/hr', label: 'HR / Payroll', icon: Wallet, module: 'employees' },
  { path: '/accounts', label: 'Accounts', icon: CreditCard, module: 'accounts' },
  { path: '/funds', label: 'Funds', icon: Wallet, module: 'funds' },
  { path: '/pnl', label: 'P&L', icon: TrendingUp, module: 'pnl' },
  { path: '/trading', label: 'Spot Trading', icon: CandlestickChart, module: 'trading' },
  { path: '/futures', label: 'Futures', icon: Zap, module: 'trading' },
  { path: '/academy', label: 'Academy', icon: GraduationCap, module: 'academy' },
  { path: '/invoices', label: 'Invoice Generator', icon: Receipt, module: 'accounting' },
  { path: '/fund-schemes', label: 'Fund Schemes', icon: Layers, module: 'performance' },
  { path: '/investment-plans', label: 'ETF Plans', icon: TrendingUp, module: 'funds' },
  { path: '/investment-plans/ledger', label: 'Plans Ledger', icon: FileText, module: 'funds' },
  { path: '/agreements', label: 'Agreements', icon: ScrollText, module: 'agreements' },
  { path: '/profit-sharing', label: 'Revenue', icon: Percent, module: 'performance' },
  { path: '/company-profit', label: 'Company P&L', icon: LineChart, module: 'company_pnl' },
  { path: '/expenses', label: 'Expenses', icon: Receipt, module: 'expenses' },
  { path: '/performance', label: 'Perf. Fees', icon: PieChart, module: 'performance' },
  { path: '/kyc', label: 'KYC', icon: ShieldCheck, module: 'kyc' },
  { path: '/sales-team', label: 'Sales Team', icon: Handshake, module: 'sales' },
  { path: '/attendance', label: 'Attendance', icon: Clock, module: 'attendance' },
  { path: '/reports', label: 'Reports', icon: BarChart3, module: 'reports' },
  { path: '/generate-reports', label: 'PDF Reports', icon: FileText, module: 'reports' },
  { path: '/data-export', label: 'Data Export', icon: Download, module: 'reports' },
  { path: '/agreement-settings', label: 'Agr. Template', icon: FilePen, module: 'settings' },
  { path: '/payment-settings', label: 'Payment Config', icon: CreditCard, module: 'settings' },
  { path: '/notices', label: 'Notices', icon: Megaphone, module: 'dashboard' },
  { path: '/permissions', label: 'Permissions', icon: Lock, module: 'permissions' },
  { path: '/settings', label: 'Settings', icon: Settings, module: 'settings' },
  { path: '/audit', label: 'Audit Logs', icon: FileText, module: 'audit' },
];

export default function Sidebar({ open, onClose }) {
  const { user, permissions } = useAuth();
  const location = useLocation();

  const filtered = navItems.filter(item => {
    const modPerms = permissions?.[item.module];
    return modPerms?.view === true;
  });

  return (
    <>
      {open && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden" onClick={onClose} />
      )}
      <aside
        data-testid="sidebar"
        className={`fixed top-0 left-0 z-50 h-full w-60 bg-[#0F1729]
          transform transition-transform duration-300 ease-out
          lg:translate-x-0 lg:static lg:z-auto
          ${open ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex items-center justify-between h-14 px-4">
          <div className="flex items-center gap-2.5">
            <img src="/quantora-logo.png" alt="Quantora" className="w-8 h-8 rounded-lg" />
            <span className="font-heading text-sm font-bold text-white/90 tracking-wide">QUANTORA</span>
          </div>
          <button onClick={onClose} className="lg:hidden text-white/40 hover:text-white/80 transition-colors" data-testid="sidebar-close-btn">
            <X size={18} />
          </button>
        </div>
        <div className="px-3 py-1">
          <div className="h-px bg-white/10" />
        </div>
        <nav className="px-3 py-2 space-y-0.5 overflow-y-auto h-[calc(100%-7rem)]">
          {filtered.map(item => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                data-testid={`nav-${item.path.slice(1)}`}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-200
                  ${active
                    ? 'bg-blue-500/20 text-blue-300 shadow-sm'
                    : 'text-white/45 hover:bg-white/[0.06] hover:text-white/80'
                  }`}
              >
                <Icon size={16} strokeWidth={1.5} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 px-4 py-3 border-t border-white/5">
          <p className="text-[10px] text-white/30 text-center tracking-[0.2em]">QUANTORA</p>
        </div>
      </aside>
    </>
  );
}
